import { animate, style, transition, trigger } from '@angular/animations';
import { CdkDrag, CdkDragEnd } from '@angular/cdk/drag-drop';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { RtSvgSpriteModule } from 'rt-svg-sprite';

export enum ConfirmLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  UNKNOWN = 'unknown',
}

/**
 * [Shared]
 * Describes data fields for confirm dialog.
 */
export interface ConfirmDialogData {
  /** Custom caption text for confirm dialog. */
  caption?: string;

  /** Custom details text for confirm dialog. */
  detailsText?: string;

  parseHTML?: boolean;

  /** Custom confirm button text. */
  confirm?: string;

  reject?: string;
  enableReject?: boolean;

  /** Custom cancel button text. */
  cancel?: string;

  /** Custom timer value. */
  enableSlider?: boolean;

  level?: ConfirmLevel;
}

/**
 * Default values for confirm dialog component.
 */
const CONFIRM_DIALOG_DEFAULT_VALUES: Required<ConfirmDialogData> = {
  caption: $localize`Are you sure you want to continue?`,
  detailsText: '',
  confirm: $localize`Accept`,
  reject: $localize`Reject`,
  enableReject: false,
  cancel: $localize`Cancel`,
  enableSlider: false,
  level: ConfirmLevel.UNKNOWN,
  parseHTML: false,
};

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styleUrls: ['./confirm-dialog.component.scss'],
  standalone: true,
  imports: [MatDialogModule, CdkDrag, NgClass, RtSvgSpriteModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in', style({ opacity: 1 }))])]),
    trigger('fadeOut', [transition(':leave', [style({ opacity: 1 }), animate('0.2s ease-in', style({ opacity: 0 }))])])
  ]
})
export class ConfirmDialogComponent {
  /** Contain question text. */
  public config: Required<ConfirmDialogData> = Object.assign({}, CONFIRM_DIALOG_DEFAULT_VALUES, null);
  public isDrag = false;
  public dragPosition = { x: 0, y: 0 };
  public confirmHover = false;

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public injectedData: ConfirmDialogData,
    private dialogRef: MatDialogRef<ConfirmDialogComponent>
  ) {
    // Write confirm dialog config as result of merge injected data with default data.
    this.config = Object.assign({}, CONFIRM_DIALOG_DEFAULT_VALUES, injectedData);
  }

  /**
   * Close dialog and return data (if provided).
   */
  public closeDialog(providedData?: boolean): void {
    this.dialogRef.close(providedData);
  }

  dragEnded(event: CdkDragEnd) {
    const element = event.source.element.nativeElement;
    const container = document.querySelector('.confirm-dialog__controls');

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (Math.floor(elementRect.left - 1) <= Math.floor(containerRect.left)) {
      this.dialogRef.close(true);
    } else {
      this.isDrag = false;
      this.dragPosition = { x: 0, y: 0 };
    }
  }

  protected readonly ConfirmLevel = ConfirmLevel;
}
